import { createStore } from 'vuex'
import analyticsService from '@/services/analyticsService'

export default createStore({
  // DSGVO-konform: Keine persistente Speicherung von Nutzerdaten
  // plugins: [createPersistedState()] - ENTFERNT für DSGVO-Konformität
  
  strict: process.env.NODE_ENV !== 'production', // Nur in Dev-Mode strict
  
  state: {
    // Benutzerinformationen - werden nicht persistent gespeichert
    user: {
      name: '',
      email: '',
      phone: '',
      company: '',
      address: ''
    },
    // Funnel-Daten - werden nicht persistent gespeichert
    funnel: {
      currentStep: 1,
      totalSteps: 3,
      type: '', // 'business' oder 'private'
      // Speichert alle Antworten des Benutzers nur während der Session
      answers: {},
      // Ergebnisse und Empfehlungen
      recommendations: [],
      // Flag, ob der Funnel abgeschlossen wurde
      completed: false
    },
    // App-Status
    appStatus: {
      loading: false,
      error: null
    }
  },
  getters: {
    // Benutzerinformationen
    userName: state => state.user.name,
    userEmail: state => state.user.email,
    userPhone: state => state.user.phone,
    userCompany: state => state.user.company,
    
    // Funnel-Status
    currentStep: state => state.funnel.currentStep,
    totalSteps: state => state.funnel.totalSteps,
    funnelProgress: state => (state.funnel.currentStep / state.funnel.totalSteps) * 100,
    funnelType: state => state.funnel.type,
    isBusinessFunnel: state => state.funnel.type === 'business',
    isPrivateFunnel: state => state.funnel.type === 'private',
    
    // Ergebnisse
    recommendations: state => state.funnel.recommendations,
    
    // App-Status
    isLoading: state => state.appStatus.loading,
    hasError: state => !!state.appStatus.error,
    errorMessage: state => state.appStatus.error
  },
  mutations: {
    // Benutzerinformationen aktualisieren
    SET_USER_INFO(state, userInfo) {
      state.user = { ...state.user, ...userInfo };
    },
    
    // Funnel-Step aktualisieren
    SET_CURRENT_STEP(state, step) {
      state.funnel.currentStep = step;
    },
    
    // Funnel-Typ setzen
    SET_FUNNEL_TYPE(state, type) {
      state.funnel.type = type;
    },
    
    // Antworten speichern
    SET_ANSWER(state, { questionId, answer }) {
      state.funnel.answers = { 
        ...state.funnel.answers, 
        [questionId]: answer 
      };
    },
    
    // Mehrere Antworten auf einmal speichern
    SET_ANSWERS(state, answers) {
      state.funnel.answers = { ...state.funnel.answers, ...answers };
    },
    
    // Empfehlungen setzen
    SET_RECOMMENDATIONS(state, recommendations) {
      state.funnel.recommendations = recommendations;
    },
    
    // Loading-Status aktualisieren
    SET_LOADING(state, isLoading) {
      state.appStatus.loading = isLoading;
    },
    
    // Fehler setzen
    SET_ERROR(state, error) {
      state.appStatus.error = error;
    },
    
    // Funnel zurücksetzen
    RESET_FUNNEL(state) {
      state.funnel.currentStep = 1;
      state.funnel.answers = {};
      state.funnel.recommendations = [];
      state.funnel.completed = false;
      // Funnel-Typ beibehalten, da er ggf. gerade gesetzt wurde
    },

    // Funnel als abgeschlossen markieren
    SET_FUNNEL_COMPLETED(state, isCompleted) {
      state.funnel.completed = isCompleted;
    }
  },
  actions: {
    // Benutzerinformationen aktualisieren
    updateUserInfo({ commit }, userInfo) {
      commit('SET_USER_INFO', userInfo);
    },
    
    // Funnel-Step aktualisieren
    setCurrentStep({ commit, state }, step) {
      commit('SET_CURRENT_STEP', step);
      
      // Analytics-Event für Funnel-Schritt
      if (process.env.NODE_ENV === 'production') {
        analyticsService.trackFunnelStep(
          state.funnel.type || 'unknown',
          step,
          `Step ${step}`
        );
      }
    },
    
    // Funnel-Typ setzen (business oder private)
    setFunnelType({ commit }, type) {
      commit('SET_FUNNEL_TYPE', type);
      
      // Analytics-Event für Funnel-Start
      if (process.env.NODE_ENV === 'production') {
        analyticsService.trackEvent(
          'funnel',
          'funnel_start',
          type
        );
      }
    },
    
    // Antwort speichern
    saveAnswer({ commit }, { questionId, answer }) {
      commit('SET_ANSWER', { questionId, answer });
    },
    
    // Mehrere Antworten auf einmal speichern
    saveAnswers({ commit }, answers) {
      commit('SET_ANSWERS', answers);
    },
    
    // Funnel-Daten an API senden und Empfehlungen erhalten
    async submitFunnelData({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        // API-Aufruf simulieren - in der realen Implementierung mit axios ersetzen
        // const response = await axios.post('/api/funnel/submit', {
        //   user: state.user,
        //   answers: state.funnel.answers
        // });
        
        // Simulierte Antwort
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Beispiel-Empfehlungen basierend auf den Antworten
        const mockRecommendations = [
          {
            id: 1,
            title: 'IT-Sicherheitspaket Basic',
            description: 'Grundlegende IT-Sicherheit für kleine Unternehmen',
            price: 'ab 99,- € / Monat',
            features: ['Firewall-Schutz', 'Antivirenlösung', 'Basis-Backup']
          },
          {
            id: 2,
            title: 'Managed Workplace',
            description: 'Vollständig verwaltete Arbeitsplatzrechner',
            price: 'ab 49,- € / Arbeitsplatz / Monat',
            features: ['Hardware-Management', 'Software-Updates', '24/7 Support']
          },
          {
            id: 3,
            title: 'Cloud-Infrastruktur',
            description: 'Skalierbare Cloud-Lösungen für Ihr Unternehmen',
            price: 'ab 199,- € / Monat',
            features: ['Office 365 Integration', 'Datensicherung', 'Remote-Zugriff']
          }
        ];
        
        commit('SET_RECOMMENDATIONS', mockRecommendations);
        commit('SET_LOADING', false);
        
        return mockRecommendations;
      } catch (error) {
        commit('SET_ERROR', error.message || 'Ein Fehler ist aufgetreten');
        commit('SET_LOADING', false);
        throw error;
      }
    },
    
    // Funnel zurücksetzen
    resetFunnel({ commit }) {
      commit('RESET_FUNNEL');
    },
    
    // Funnel als abgeschlossen markieren
    completeFunnel({ commit, state }, isCompleted = true) {
      commit('SET_FUNNEL_COMPLETED', isCompleted);
      
      // Analytics-Event für Funnel-Abschluss
      if (process.env.NODE_ENV === 'production' && isCompleted) {
        analyticsService.trackEvent(
          'funnel',
          'funnel_complete',
          state.funnel.type || 'unknown'
        );
      }
    }
  },
  modules: {
    // Für später, falls wir den Store in Module aufteilen wollen
  }
}) 